var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import classNames from 'classnames';
import Icon from "@icon/Icon";
import { hasValue } from "@utils";
import { hasIcon } from "@icon/utils";
/**
 * @export
 * Enhanced Button component with multiple variants and icon support
 *
 * @example Basic button
 * ```
 * <ButtonComponent title="Click me" onClick={() => console.log('clicked')} />
 * ```
 *
 * @example Button with icons
 * ```
 * <ButtonComponent
 *   title="Save"
 *   leftIcon="💾"
 *   rightIcon="→"
 *   onClick={handleSave}
 * />
 * ```
 *
 * @example Icon only button
 * ```
 * <ButtonComponent
 *   leftIcon="×"
 *   iconOnly
 *   variant="danger"
 *   onClick={handleDelete}
 * />
 * ```
 *
 * @example Async button with loading
 * ```
 * <ButtonComponent
 *   title="Submit"
 *   loadingTitle="Submitting..."
 *   onClick={async () => await submitForm()}
 * />
 * ```
 */
const ButtonComponent = (props) => {
    const { title, leftIcon, rightIcon, className, onClick, onError, loading = false, loadingTitle, active = false, disabled = false, styles, leftIconStyles, rightIconStyles, type = 'button', variant = 'primary', iconOnly = false, size = 'medium' } = props;
    const [isLoading, setIsLoading] = React.useState(false);
    const isLoadingState = loading || isLoading;
    const handleClick = (e) => __awaiter(void 0, void 0, void 0, function* () {
        if (disabled || isLoadingState || !onClick)
            return;
        try {
            setIsLoading(true);
            yield onClick(e);
        }
        catch (error) {
            console.error('Button onClick error:', error);
            onError === null || onError === void 0 ? void 0 : onError(e);
        }
        finally {
            setIsLoading(false);
        }
    });
    const renderContent = () => {
        if (iconOnly) {
            return _jsx("div", { className: "uxpcore_button__icon", style: leftIconStyles || rightIconStyles || {}, children: _jsx(Icon, { icon: (leftIcon || rightIcon), isLoading: isLoadingState }) });
        }
        const displayTitle = (isLoadingState && hasValue(loadingTitle)) ? loadingTitle : title || '';
        const leftIconElement = (hasIcon(leftIcon)) ? _jsx(Icon, { icon: leftIcon, isLoading: isLoadingState }) : null;
        const rightIconElement = (hasIcon(rightIcon)) ? _jsx(Icon, { icon: rightIcon, isLoading: isLoadingState }) : null;
        return (_jsxs(_Fragment, { children: [!!leftIconElement ? _jsx("div", { className: "uxpcore_button__icon", style: leftIconStyles, children: leftIconElement }) : null, !!displayTitle ? _jsx("div", { className: "uxpcore_button__text", children: displayTitle }) : null, !!displayTitle && !!rightIconElement ? _jsx("div", { className: "uxpcore_button__icon", style: rightIconStyles, children: rightIconElement }) : null] }));
    };
    const buttonClasses = classNames('uxpcore_button', className, {
        [`uxpcore_button--${variant}`]: variant,
        [`uxpcore_button--${size}`]: size !== 'medium',
        'uxpcore_button--active': active,
        'uxpcore_button--disabled': disabled,
        'uxpcore_button--loading': isLoadingState,
        'uxpcore_button--icon-only': iconOnly,
        'uxpcore_button--has-left-icon': leftIcon && !iconOnly,
        'uxpcore_button--has-right-icon': rightIcon && !iconOnly,
    });
    return (_jsx("button", { className: buttonClasses, style: styles, onClick: handleClick, type: type, disabled: disabled, children: renderContent() }));
};
export default ButtonComponent;
